/**
 * Frame Card Renderer (Global)
 * Automatically renders uploaded customer images inside frame product cards
 * on ANY page (collections, featured collections, recommendations, etc.).
 * Reads image from IndexedDB (shared with configurator + collection upload).
 * Exposes window.frameCardRenderer for use by collection-frame-upload.js.
 */
(function () {
  'use strict';

  var TARGET_AREA = 1200; // ~30*40 cm²

  var state = {
    blobUrl: null,
    firebaseUrl: null,
    imageAspectRatio: null
  };

  /* --- IndexedDB (same DB as configurator: 'fc-images') --- */
  function loadImageFromIDB(callback) {
    try {
      var req = indexedDB.open('fc-images', 1);
      req.onupgradeneeded = function (e) { e.target.result.createObjectStore('blobs'); };
      req.onsuccess = function (e) {
        var db = e.target.result;
        var tx = db.transaction('blobs', 'readonly');
        var store = tx.objectStore('blobs');
        var getBlob = store.get('uploadedImage');
        var getUrl = store.get('firebaseUrl');
        var result = { blob: null, firebaseUrl: null };
        getBlob.onsuccess = function () { result.blob = getBlob.result || null; };
        getUrl.onsuccess = function () { result.firebaseUrl = getUrl.result || null; };
        tx.oncomplete = function () { callback(result); };
        tx.onerror = function () { callback(result); };
      };
      req.onerror = function () { callback({ blob: null, firebaseUrl: null }); };
    } catch (e) { callback({ blob: null, firebaseUrl: null }); }
  }

  /* --- Dimension calculation --- */
  function calculateDimensions(aspectRatio) {
    var ar = Math.max(0.3, Math.min(3.0, aspectRatio));
    var w = Math.sqrt(TARGET_AREA * ar);
    var h = Math.sqrt(TARGET_AREA / ar);
    return { width: Math.round(w), height: Math.round(h) };
  }

  /* --- SVG rendering for frame cards --- */
  function renderAllFrameCards() {
    if (!state.blobUrl || !state.imageAspectRatio) return;
    var cards = document.querySelectorAll('.card-wrapper[data-frame-product]');
    for (var i = 0; i < cards.length; i++) {
      renderFrameCard(cards[i]);
    }
  }

  function renderFrameCard(cardWrapper) {
    if (!state.blobUrl || !state.imageAspectRatio) return;
    if (typeof window.generateFramePreviewSvg !== 'function') return;

    var frameLR = cardWrapper.getAttribute('data-frame-lr');
    var frameTB = cardWrapper.getAttribute('data-frame-tb');
    var frameWidth = parseFloat(cardWrapper.getAttribute('data-frame-width')) || 24;

    if (!frameLR && !frameTB) return;

    var dims = calculateDimensions(state.imageAspectRatio);
    var hasPP = cardWrapper.hasAttribute('data-frame-has-pp');

    var frameSpec = {
      imageUrl: state.blobUrl,
      frameConfig: {
        frameImageLR: frameLR,
        frameImageTB: frameTB || frameLR,
        frameWidth: frameWidth
      },
      matConfig: hasPP
        ? { type: 'standard', width: 'standard' }
        : { type: 'none' },
      dimensions: dims,
      rotation: 0
    };

    var svgMarkup = window.generateFramePreviewSvg(frameSpec);

    var mediaEl = cardWrapper.querySelector('.media.media--hover-effect');
    if (!mediaEl) return;

    if (!cardWrapper._originalMediaHTML) {
      cardWrapper._originalMediaHTML = mediaEl.innerHTML;
    }

    var secondPicture = mediaEl.querySelectorAll('picture');
    var secondPictureHTML = '';
    if (secondPicture.length > 1) {
      secondPictureHTML = secondPicture[1].outerHTML;
    }

    mediaEl.innerHTML =
      '<div class="cfu-svg-wrap">' + svgMarkup + '</div>' +
      secondPictureHTML;

    var svgEl = mediaEl.querySelector('.cfu-svg-wrap svg');
    if (svgEl) {
      svgEl.removeAttribute('width');
      svgEl.removeAttribute('height');
    }

    cardWrapper.classList.add('has-frame-upload');
  }

  function restoreAllFrameCards() {
    var cards = document.querySelectorAll('.card-wrapper[data-frame-product]');
    for (var i = 0; i < cards.length; i++) {
      var cw = cards[i];
      var mediaEl = cw.querySelector('.media.media--hover-effect');
      if (mediaEl && cw._originalMediaHTML) {
        mediaEl.innerHTML = cw._originalMediaHTML;
        cw._originalMediaHTML = null;
      }
      cw.classList.remove('has-frame-upload');
    }
  }

  /* --- MutationObserver for dynamically added cards --- */
  function observeDOM() {
    var observer = new MutationObserver(function (mutations) {
      if (!state.blobUrl) return;
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var i = 0; i < added.length; i++) {
          var node = added[i];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('.card-wrapper[data-frame-product]')) {
            renderFrameCard(node);
          }
          var nested = node.querySelectorAll ? node.querySelectorAll('.card-wrapper[data-frame-product]') : [];
          for (var j = 0; j < nested.length; j++) {
            renderFrameCard(nested[j]);
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* --- Configurator handoff --- */
  function interceptProductLinks() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="/pages/konfigurator"]') || e.target.closest('.card-wrapper[data-frame-product] a');
      if (!link) return;

      // Transfer image URL
      if (state.firebaseUrl || state.blobUrl) {
        var transferUrl = state.firebaseUrl || state.blobUrl;
        try { sessionStorage.setItem('fc-transfer-imageUrl', transferUrl); } catch (ex) { /* ignore */ }
      }

      // If product has PP, add ppType=einfach to URL so configurator pre-selects it
      var cardWrapper = link.closest('.card-wrapper[data-frame-product]');
      if (cardWrapper && cardWrapper.hasAttribute('data-frame-has-pp')) {
        var href = link.getAttribute('href');
        if (href && href.indexOf('ppType=') === -1) {
          link.setAttribute('href', href + (href.indexOf('?') !== -1 ? '&' : '?') + 'ppType=einfach');
        }
      }
    });
  }

  /* --- Public API (used by collection-frame-upload.js) --- */
  window.frameCardRenderer = {
    /** Called by collection upload after new file is selected */
    setImage: function (blobUrl, firebaseUrl, aspectRatio) {
      state.blobUrl = blobUrl;
      state.firebaseUrl = firebaseUrl;
      state.imageAspectRatio = aspectRatio;
      renderAllFrameCards();
    },
    /** Called by collection upload when image is removed */
    clearImage: function () {
      if (state.blobUrl && state.blobUrl.indexOf('blob:') === 0) {
        URL.revokeObjectURL(state.blobUrl);
      }
      state.blobUrl = null;
      state.firebaseUrl = null;
      state.imageAspectRatio = null;
      restoreAllFrameCards();
    },
    /** Get current state */
    getState: function () {
      return { blobUrl: state.blobUrl, firebaseUrl: state.firebaseUrl, imageAspectRatio: state.imageAspectRatio };
    },
    /** Re-render all cards (e.g. after PP attribute change) */
    rerender: function () {
      if (state.blobUrl && state.imageAspectRatio) renderAllFrameCards();
    }
  };

  /* --- Init: restore from IDB on every page load --- */
  observeDOM();
  interceptProductLinks();

  // Check if any frame cards exist on this page
  var hasCards = document.querySelectorAll('.card-wrapper[data-frame-product]').length > 0;
  if (!hasCards) return;

  loadImageFromIDB(function (data) {
    if (!data.blob && !data.firebaseUrl) return;

    if (data.blob) {
      state.blobUrl = URL.createObjectURL(data.blob);
      if (data.firebaseUrl) state.firebaseUrl = data.firebaseUrl;

      var img = new Image();
      img.onload = function () {
        state.imageAspectRatio = img.naturalWidth / img.naturalHeight;
        renderAllFrameCards();
      };
      img.src = state.blobUrl;
    } else if (data.firebaseUrl) {
      state.firebaseUrl = data.firebaseUrl;
      state.blobUrl = data.firebaseUrl;

      var img2 = new Image();
      img2.crossOrigin = 'anonymous';
      img2.onload = function () {
        state.imageAspectRatio = img2.naturalWidth / img2.naturalHeight;
        renderAllFrameCards();
      };
      img2.src = data.firebaseUrl;
    }
  });
})();
